<template>
  <div
    v-if="data.showTasks"
    :key="data.reloadKey"
    class="i-checklist"
    :class="{ mini: isCoachingConfirmedPage }"
  >
    <div class="sub">
      <div
        v-if="!isCoachingConfirmedPage"
        class="main"
      >
        Get ready for your coaching session
      </div>
      <div
        v-if="isCoachingConfirmedPage"
        class="main"
      >
        Your career report is ready and {{ assignedCoachFirstName }} is standing by!
      </div>
      {{ !isCoachingConfirmedPage ? "To make the best out of your session, please complete the following steps" : "" }}
    </div>
    <iTaskBox
      v-for="(step, index) in steps.filter((e) => e.isVisible)"
      :key="step.key"
      :index="index + 1"
      :header="step.header"
      :subheader="step.subheader"
      :action-text="step.actionText"
      :task-key="step.key"
      :is-checked="step.isChecked"
      @action-clicked="step.action"
    >
      <template #collapsible>
        <template v-if="step.key === 'booking'">
          <div class="coach">
            <div class="picture-name">
              <img
                class="picture"
                :src="assignedCoach.profilePhotoUrl || ''"
              />
              <div class="right">
                <div class="name">
                  {{ assignedCoach.fullName }}
                  <template v-if="!!assignedCoach?.fullName && isMdOrLarger">
                    <div class="top-pick">Top pick: {{ assignedCoach?.percentMatch }}% Match</div>
                  </template>
                </div>
                <div class="rating">
                  <img
                    alt="5 stars rating"
                    :src="iconStars"
                  />
                  {{ assignedCoach?.rating }} stars
                </div>
              </div>
            </div>
            <iBaseExpandableParagraph
              class="bio"
              :text="assignedCoach?.biography || ''"
              text-size="unset"
              :collapsed-length="100"
              :minified="true"
            />
            <div class="keys">
              <div class="header">
                {{ assignedCoachFirstName || "Your Coach" }} {{ !isCoachingConfirmedPage ? "will" : "can" }} help with:
              </div>
              <div class="perks">
                <div
                  v-for="item in LIST_ITEMS"
                  :key="item"
                  class="item"
                >
                  <img :src="iconTick" /> {{ item }}
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-if="step.key === 'video'">
          <div class="video">
            <iframe
              src="https://player.vimeo.com/video/1086537733?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;playsinline=0"
              width="100%"
              frameborder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              title="Coaching Welcome Video"
              style="position: absolute; top: 0; left: 0; width: 100%; height: 100%"
            ></iframe>
          </div>
        </template>
        <template v-if="step.key === 'reminder'">
          <div
            class="card"
            :style="{
              '--sc-color': amethyst600,
              '--bd-color': amethyst100,
              '--bg-color': purpleHeart50,
              '--qt-color': amethyst800
            }"
          >
            <img :src="iconLaurel" />
            <div class="quote">
              "94% of our clients achieve their career goals within 6 months of their first coaching session."
              <div class="source">— Jobtest Career Success Study {{ new Date().getFullYear() - 1 }}</div>
            </div>
          </div>
        </template>
        <template v-if="step.key === 'careerTest'">
          <div
            class="career-test"
            :style="{ '--bd-color': astronaut100 }"
          >
            <div class="badge"><img :src="iconSparkle" /> AI-Powered Insights</div>
            <div>Get tailored career advice based on your profile</div>
            <div class="badges">
              <div :style="{ '--bd-color': amethyst400 }"><img :src="iconSpike" /> Career Growth</div>
              <div :style="{ '--bd-color': astronaut500 }"><img :src="iconStrike" /> AI Insights</div>
            </div>
          </div>
        </template>
        <template v-if="step.key === 'resources'">
          <div
            class="card"
            :style="{
              '--sc-color': astronaut600,
              '--bd-color': astronaut100,
              '--bg-color': astronaut50,
              '--qt-color': astronaut800
            }"
          >
            <img
              class="icon"
              :src="iconStar"
            />
            <div class="quote">
              "Clients who send their materials ahead of time report 2x better results from their coaching sessions."
              <div class="source">— Linda Lutz, Career Coach</div>
            </div>
          </div>
        </template>
      </template>
    </iTaskBox>
    <iBaseModal
      class="embed"
      :visible="data.showCalcomEmbedModal"
      :fully-customisable="true"
      max-width="100%"
      @click-on-close="data.showCalcomEmbedModal = false"
    >
      <div class="confirmation-modal">
        <iCalcomScheduler
          :key="data.calcomEmbedLink"
          :on-booking-successful="bookingSuccessful"
          :embed-link="data.calcomEmbedLink"
          version="inline"
        />
      </div>
    </iBaseModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useUserStore } from "@/stores/user"
import { useCoachingStore } from "@/stores/coaching"
import {
  amethyst100,
  amethyst400,
  amethyst600,
  amethyst800,
  astronaut100,
  astronaut50,
  astronaut500,
  astronaut600,
  astronaut800,
  purpleHeart50
} from "@/styles/colors"
import { breakpointMd } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"
import { GlobalVariables } from "@/helpers/globalVariables"
// import api from "@/services/apiClient"
// import UrlService from "@/services/urlService"

import iBaseExpandableParagraph from "@/components/base/BaseExpandableParagraph.vue"
import iBaseModal from "@/components/base/BaseModal.vue"
import iTaskBox from "@/components/coaching/confirmation/TaskBox.vue"
import iCalcomScheduler from "@/components/global/CalcomScheduler.vue"

// ASSETS
import iconTick from "@/assets/icons/icon-green-check.svg"
import iconStars from "@/assets/icons/icon-five-slashed-stars.svg"
import iconStar from "@/assets/icons/icon-blue-star.svg"
import iconLaurel from "@/assets/icons/icon-purple-laurel.svg"
import iconSparkle from "@/assets/icons/icon-purple-sparkle.svg"
import iconStrike from "@/assets/icons/icon-blue-strike.svg"
import iconSpike from "@/assets/icons/icon-purple-spike.svg"

// TYPES
import CalcomBooking from "@/shared/types/CalcomBooking"

// STORES AND COMPOSABLES
const userStore = useUserStore()
const coachingStore = useCoachingStore()
const isMdOrLarger = useIsScreenSize(breakpointMd)
const router = useRouter()

// CONSTANTS
const LIST_ITEMS = [
  "Career Transition Support",
  "Resume, Cover Letter and LinkedIn Review",
  "Job Application Support",
  "Interview Preparation"
]
// const localeDateStringOptions = {
//   year: "numeric",
//   month: "long",
//   day: "numeric",
//   timeZoneName: "short",
//   hour: "2-digit",
//   minute: "2-digit"
// } as const

// ROUTES
const activeRoute = useRoute()

// DATA
const data = reactive({
  upcomingBooking: null as CalcomBooking | null,
  showTasks: false,
  showCalcomEmbedModal: false,
  calcomEmbedLink: "",
  reloadKey: 0
})

// COMPUTED
const assignedCoach = computed(() => coachingStore.getAssignedCoach)
const assignedCoachFirstName = computed(() => assignedCoach.value?.fullName?.split(" ")[0])
const isCoachingConfirmedPage = computed(
  () => activeRoute.name === GlobalVariables.urls.coaching.children.confirmed.routeName
)
// const hasUpcomingBookingOrCredits = computed(
//   () =>
//     !!data.upcomingBooking ||
//     !!userStore.getFirstRecurringCredits ||
//     !!userStore.getScopingCredits ||
//     !!userStore.getOnDemandCredits
// )
const steps = computed(() => {
  return [
    {
      isChecked: !!data.upcomingBooking,
      key: "booking",
      isVisible: true,
      header: !isCoachingConfirmedPage.value
        ? `Head to your Coaching Dashboard to view your meeting with ${assignedCoachFirstName.value}`
        : "Head to your dashboard and pick a time",
      // hasUpcomingBookingOrCredits.value
      // ? data.upcomingBooking
      //   ? `${data.upcomingBooking.length}-Minute ${data.upcomingBooking.title}`
      //   : `Book your ${userStore.getScopingCredits ? "Discovery" : userStore.getFirstRecurringCredits ? "first Subscription" : "On-Demand"} session with your coach`
      // : "",
      subheader: !isCoachingConfirmedPage.value
        ? "If your meeting had expired and you do not see it show up on your dashboard after a few minutes, you can easily schedule another one right there."
        : `Your career report is inside. Start digging in, then pick a time with ${assignedCoachFirstName.value}. She'll break down your results, show you your stongest move, and build your action plan together.`,
      // data.upcomingBooking
      // ? new Date(data.upcomingBooking?.startTime).toLocaleString("en-US", localeDateStringOptions)
      // : "",
      actionText: !isCoachingConfirmedPage.value ? "View Coaching Dashboard" : "Go to my Dashboard",
      // !data.upcomingBooking &&
      // (userStore.getFirstRecurringCredits || userStore.getScopingCredits || userStore.getOnDemandCredits)
      //   ? "Book Your Session"
      //   : "",
      action: goToCoachingHome // openCalcomModal
    },
    {
      key: "video",
      isVisible: !isCoachingConfirmedPage.value,
      header: "Watch a Welcome Message From our Head Coach"
    },
    {
      key: "reminder",
      isVisible: !isCoachingConfirmedPage.value,
      header: "Set a Reminder",
      subheader:
        "Make sure the session is on your calendar and <strong>set an alarm</strong>.  Your coach is reserving this time just for you—let’s make it count."
    },
    {
      isChecked: userStore.isFeedbackExperienceCompleted,
      key: "careerTest",
      isVisible: !isCoachingConfirmedPage.value,
      header: userStore.isReportUser ? "Explore Your Career Report" : "Take the Career Test",
      subheader: "You will review this in your first session with your coach.",
      actionText: userStore.isReportUser ? "Explore Career Report" : "Start Career Test",
      action: () =>
        router.push({
          name: userStore.isReportUser
            ? GlobalVariables.urls.report.children.home.routeName
            : GlobalVariables.urls.report.children.testRetake.routeName
        })
    },
    {
      key: "resources",
      isVisible: !isCoachingConfirmedPage.value,
      header: "Send Your Resources",
      subheader: `Watch for an email from Coach ${assignedCoachFirstName.value} and reply with your resume and LinkedIn url before your session.`
    }
  ]
})

// FUNCTIONS
/**
 * Fetches the most recent of user's upcoming coaching/on-demand booking
 */
// const fetchUserUpcomingBooking = async () => {
//   try {
//     const upcomingBooking = await api.booking.getUpcoming({
//       bookingEventTypes: ["coaching", "onDemand"],
//       isPaid: true
//     })
//     data.upcomingBooking = upcomingBooking.booking
//   } catch (error) {
//     console.error("Error fetching user's upcoming coaching/on-demand booking:", error)
//   }
// }

const goToCoachingHome = () => {
  router.push({
    name: GlobalVariables.urls.coaching.children.home.routeName
  })
}

// const openCalcomModal = () => {
//   const calcomUsername = assignedCoach.value.calcomUsername
//   if (!calcomUsername) return

//   data.calcomEmbedLink = UrlService.generateCalComEmbedLink(
//     `${calcomUsername}/${userStore.getScopingCredits ? "intro" : userStore.getFirstRecurringCredits ? "first-subscription-session" : "on-demand"}`
//   )
//   data.showCalcomEmbedModal = true
// }

const bookingSuccessful = async () => {
  localStorage.removeItem("checklist-reminder-checked")
  window.dispatchEvent(new CustomEvent("localstorage-checklist-item-changed", { detail: { key: "reminder" } }))
  data.reloadKey += 1
  // await fetchUserUpcomingBooking()
  data.showCalcomEmbedModal = false
}

// LIFECYCLE HOOKS
onMounted(async () => {
  // await fetchUserUpcomingBooking()
  data.showTasks = true
})
</script>

<style scoped lang="scss">
.i-checklist {
  @include flex(column, start, stretch, $gap: 24px);

  color: $black;
  min-height: calc(100vh - 128px);

  &.mini {
    min-height: unset;
  }

  .sub {
    @include flex(column, start, center);
    @include text-lg;

    font-weight: 400;
    text-align: center;

    .main {
      @include text-xl;

      font-weight: 700;
    }
  }

  .coach {
    @include flex(column, start, stretch, $gap: 10px);

    border-top: 1px solid $zumthor100;
    padding-top: 20px;

    .picture-name {
      @include flex(row, start, center, $gap: 16px);

      .picture {
        @include border-radius-rounded;

        width: 70px;
        height: 70px;
      }

      .right {
        @include flex(column, start, stretch, $gap: 10px);

        .rating {
          font-weight: 700;

          img {
            vertical-align: text-bottom;
            margin-right: 4px;
          }
        }

        .name {
          @include flex(row, start, stretch, $gap: 16px);
          @include text-md;

          font-weight: 600;

          .top-pick {
            @include border-radius-xxl;
            @include text-xs;

            width: fit-content;
            padding: 2px 10px;
            color: $white;
            text-transform: uppercase;
            background-image: $blue-purple-gradient;
          }
        }
      }
    }

    .bio {
      color: $comet600;

      @media screen and (min-width: $breakpoint-md) {
        padding-left: 86px;
      }
    }

    .keys {
      @include flex(column, start, stretch, $gap: 9px);

      @media screen and (min-width: $breakpoint-md) {
        padding-left: 86px;
      }

      .header {
        font-weight: 600;
      }

      .perks {
        @include flex(row, start, stretch, wrap, 10px 18px);
      }
    }
  }

  .video {
    padding: 55% 0 0;
    position: relative;
  }

  .card {
    @include flex(row, start, start, $gap: 16px);
    @include border-radius-sm;

    width: 100%;
    padding: 24px;
    border: 1px solid var(--bd-color);
    color: var(--qt-color);
    background-color: var(--bg-color);
    box-shadow: 0 1px 2px 0 #0000000d;

    .icon {
      width: 24px;
      height: 24px;
      flex: 0 0 24px;
    }

    .quote {
      @include flex(column, start, start, $gap: 10px);
      @include text-md;

      font-weight: 600;

      .source {
        @include text-sm;

        font-weight: 400;
        font-style: italic;
        color: var(--sc-color);
      }
    }
  }

  .career-test {
    @include flex(column, start, center, $gap: 16px);
    @include border-radius-md;
    @include text-md;

    width: 100%;
    font-weight: 600;
    padding: 24px;
    border: 1px solid var(--bd-color);
    background-image: linear-gradient(90deg, #4a9dff0d 0%, #be4fff0d 100%);
    box-shadow: 0 1px 2px 0 #0000000d;
    text-align: center;

    .badge {
      @include flex(row, center, center, $gap: 8px);
      @include text-sm;
      @include border-radius-xxl;

      width: fit-content;
      background-image: linear-gradient(90deg, #f3e8ff 0%, #dbeafe 100%);
      padding: 8px 16px;
      color: $amethyst800;
    }

    .badges {
      @include grid($cols: 1fr, $rows: auto, $align-items: center, $gap: 16px);

      text-align: start;

      @media screen and (min-width: $breakpoint-md) {
        @include grid($cols: 1fr 1fr, $rows: 1fr, $align-items: center, $gap: 16px);
      }

      > div {
        @include flex(row, start, center, $gap: 10px);
        @include border-radius-md;

        padding: 16px;
        border: 1px solid var(--bd-color);
        background-color: white;
      }
    }
  }

  .embed {
    padding: 30px 0;

    @media screen and (min-width: $breakpoint-lg) {
      padding: unset;
    }

    .confirmation-modal {
      @include flex($direction: column, $justify: start, $align: center);

      margin: 10px;

      @media screen and (min-width: $breakpoint-md) {
        margin: 25px;
      }
    }
  }
}
</style>
